
import React from 'react';
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from '@/context/AuthContext';
import { CompanyProvider } from '@/context/CompanyContext';
import { ThemeProvider } from '@/context/ThemeContext';
import { FontSizeProvider } from '@/context/FontSizeContext';
import { SubscriptionProvider } from '@/context/SubscriptionContext';
import { OnboardingGuard } from '@/components/auth/OnboardingGuard';
import { ErrorBoundary } from "@/components/common/ErrorBoundary";
import { SupabaseInventoryProvider } from "@/context/SupabaseInventoryContext";
import { RoleGuard } from "@/components/auth/RoleGuard";
import { ProtectedRoute, PublicRoute } from "@/components/auth/RouteGuards";
import { PlanRouter } from "@/components/auth/PlanRouter";
import { PlanRouteGuard } from "@/components/auth/PlanRouteGuard";
import Index from "./pages/Index";
import { Auth } from "./pages/Auth";
import { Dashboard } from "./pages/Dashboard";
import { CompanyHistory } from "./pages/CompanyHistory";
import { ExportImport } from "./pages/ExportImport";
import { Subscription } from "./pages/Subscription";
import { PaymentSuccess } from "./pages/PaymentSuccess";
import { PaymentCanceled } from "./pages/PaymentCanceled";

import { Settings } from "./pages/Settings";
import { Onboarding } from "./pages/Onboarding";
import { TeamManagement } from "./pages/TeamManagement";
import NotFound from "./pages/NotFound";
import InviteCapture from "@/components/auth/InviteCapture";
import { MentionsLegales } from './pages/legal/MentionsLegales';
import { PolitiqueConfidentialite } from './pages/legal/PolitiqueConfidentialite';
import { ConditionsUtilisation } from './pages/legal/ConditionsUtilisation';
import { ConditionsVente } from './pages/legal/ConditionsVente';
import { PolitiqueCookies } from './pages/legal/PolitiqueCookies';
import { ConformiteLogicielle } from './pages/legal/ConformiteLogicielle';
import { SessionTimeoutHandler } from '@/components/security/SessionTimeoutHandler';
import { CaisseWithSidebar } from "./pages/CaisseWithSidebar";
import { Analytics } from "./pages/Analytics";
import { RetraitAnalytics } from "./pages/RetraitAnalytics";
import { RetraitWithSidebar } from "./pages/RetraitWithSidebar";
import { Statistics } from "./pages/Statistics";
import ApiKeys from "./pages/ApiKeys";
import AdminDashboard from "./pages/AdminDashboard";
import { AuditLogs } from "./pages/AuditLogs";
import { Setup2FA } from "./pages/Setup2FA";
import { AdminGuard } from "@/components/auth/AdminGuard";
import { LegalBanner } from "./components/legal/LegalBanner";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { FloatingScannerButton } from "@/components/inventory/FloatingScannerButton";

const queryClient = new QueryClient();

const App = () => (
  <ErrorBoundary>
    <ThemeProvider>
      <FontSizeProvider>
        <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <SubscriptionProvider>
            <CompanyProvider>
              <SupabaseInventoryProvider>
                <TooltipProvider>
                  <BrowserRouter>
                    <InviteCapture />
                    <SessionTimeoutHandler />
                    <Routes>
                      <Route path="/" element={
                        <ProtectedRoute>
                          <OnboardingGuard>
                            <PlanRouter>
                              <RoleGuard>
                                <Dashboard />
                              </RoleGuard>
                            </PlanRouter>
                          </OnboardingGuard>
                        </ProtectedRoute>
                      } />
                      <Route path="/auth" element={
                        <PublicRoute>
                          <Auth />
                        </PublicRoute>
                      } />
                      <Route path="/landing" element={
                        <PublicRoute>
                          <Index />
                        </PublicRoute>
                      } />
                      <Route path="/dashboard" element={
                        <ProtectedRoute>
                          <OnboardingGuard>
                            <PlanRouteGuard requirePremium>
                              <RoleGuard>
                                <Dashboard />
                              </RoleGuard>
                            </PlanRouteGuard>
                          </OnboardingGuard>
                        </ProtectedRoute>
                      } />
                      <Route path="/caisse" element={
                        <ProtectedRoute>
                          <OnboardingGuard>
                            <PlanRouteGuard requirePremium>
                              <CaisseWithSidebar />
                            </PlanRouteGuard>
                          </OnboardingGuard>
                        </ProtectedRoute>
                      } />
                      <Route path="/retrait" element={
                        <ProtectedRoute>
                          <OnboardingGuard>
                            <PlanRouteGuard requirePremium>
                              <RetraitWithSidebar />
                            </PlanRouteGuard>
                          </OnboardingGuard>
                        </ProtectedRoute>
                      } />
                      <Route path="/history" element={
                        <ProtectedRoute>
                          <OnboardingGuard>
                            <PlanRouteGuard requirePremium>
                              <RoleGuard>
                                <CompanyHistory />
                              </RoleGuard>
                            </PlanRouteGuard>
                          </OnboardingGuard>
                        </ProtectedRoute>
                      } />
                      <Route path="/team" element={
                        <ProtectedRoute>
                          <OnboardingGuard>
                            <PlanRouteGuard requirePremium>
                              <RoleGuard>
                                <TeamManagement />
                              </RoleGuard>
                            </PlanRouteGuard>
                          </OnboardingGuard>
                        </ProtectedRoute>
                      } />
                      <Route path="/export-import" element={
                        <ProtectedRoute>
                          <OnboardingGuard>
                            <PlanRouteGuard requirePremium>
                              <RoleGuard>
                                <ExportImport />
                              </RoleGuard>
                            </PlanRouteGuard>
                          </OnboardingGuard>
                        </ProtectedRoute>
                      } />
                      <Route path="/analytics" element={
                        <ProtectedRoute>
                          <OnboardingGuard>
                            <PlanRouteGuard requirePremium>
                              <RoleGuard>
                                <Analytics />
                              </RoleGuard>
                            </PlanRouteGuard>
                          </OnboardingGuard>
                        </ProtectedRoute>
                      } />
                      <Route path="/retrait-analytics" element={
                        <ProtectedRoute>
                          <OnboardingGuard>
                            <PlanRouteGuard requirePremium>
                              <RoleGuard>
                                <RetraitAnalytics />
                              </RoleGuard>
                            </PlanRouteGuard>
                          </OnboardingGuard>
                        </ProtectedRoute>
                      } />
                      <Route path="/statistics" element={
                        <ProtectedRoute>
                          <OnboardingGuard>
                            <RoleGuard>
                              <Statistics />
                            </RoleGuard>
                          </OnboardingGuard>
                        </ProtectedRoute>
                      } />
                      <Route path="/api-keys" element={
                        <ProtectedRoute>
                          <OnboardingGuard>
                            <RoleGuard>
                              <ApiKeys />
                            </RoleGuard>
                          </OnboardingGuard>
                        </ProtectedRoute>
                      } />
                      <Route path="/admin" element={
                        <ProtectedRoute>
                          <OnboardingGuard>
                            <AdminGuard>
                              <AdminDashboard />
                            </AdminGuard>
                          </OnboardingGuard>
                        </ProtectedRoute>
                      } />
                      <Route path="/audit-logs" element={
                        <ProtectedRoute>
                          <OnboardingGuard>
                            <AdminGuard>
                              <AuditLogs />
                            </AdminGuard>
                          </OnboardingGuard>
                        </ProtectedRoute>
                      } />
                      <Route path="/setup-2fa" element={
                        <ProtectedRoute>
                          <OnboardingGuard>
                            <Setup2FA />
                          </OnboardingGuard>
                        </ProtectedRoute>
                      } />
                      <Route path="/subscription" element={
                        <ProtectedRoute>
                          <OnboardingGuard>
                            <RoleGuard>
                              <Subscription />
                            </RoleGuard>
                          </OnboardingGuard>
                        </ProtectedRoute>
                      } />
                      <Route path="/payment-success" element={<PaymentSuccess />} />
                      <Route path="/payment-canceled" element={<PaymentCanceled />} />
                      <Route path="/settings" element={
                        <ProtectedRoute>
                          <OnboardingGuard>
                            <RoleGuard>
                              <Settings />
                            </RoleGuard>
                          </OnboardingGuard>
                        </ProtectedRoute>
                      } />
                      <Route path="/onboarding" element={
                        <ProtectedRoute>
                          <Onboarding />
                        </ProtectedRoute>
                      } />
                      {/* Pages légales - Accès public */}
                      <Route path="/legal/mentions" element={<MentionsLegales />} />
                      <Route path="/legal/privacy" element={<PolitiqueConfidentialite />} />
                      <Route path="/legal/terms" element={<ConditionsUtilisation />} />
                      <Route path="/legal/sales" element={<ConditionsVente />} />
                      <Route path="/legal/cookies" element={<PolitiqueCookies />} />
                      <Route path="/legal/compliance" element={<ConformiteLogicielle />} />
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                    <LegalBanner />
                    <FloatingScannerButton />
                    <Toaster />
                    <Sonner />
                  </BrowserRouter>
                </TooltipProvider>
              </SupabaseInventoryProvider>
            </CompanyProvider>
          </SubscriptionProvider>
        </AuthProvider>
      </QueryClientProvider>
      </FontSizeProvider>
    </ThemeProvider>
  </ErrorBoundary>
);

export default App;
